import { useEffect, useState } from "react";
import Link from "@mui/material/Link";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import TokenManager from "../../commons/TokenManager";
import { getAuthenticatedRedirectUrl, getFullPath } from "../../commons/helpers";
import OtpInput from "../components/OtpInput";
import useAuth from "../../services/useAuth";
import useUsers from "../../services/useUsers";
import { useAuthContext } from "../../contexts/Auth";

const theme = createTheme();

function formatCountdown(totalSeconds: number) {
    const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");

    return `${minutes}:${seconds}`;
}

export default function EmailVerification() {
    const { isAuthenticated } = useAuthContext();
    const { refreshAccessToken } = useAuth();
    const { requestVerificationEmail, validateUserEmail } = useUsers();

    const [code, setCode] = useState("");
    const [email, setEmail] = useState("");
    const [hasRequestedCode, setHasRequestedCode] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isSubmittingCode, setIsSubmittingCode] = useState(false);

    useEffect(() => {
        async function sendCodeOnEntry() {
            if (!isAuthenticated || hasRequestedCode) {
                return;
            }

            const tokenData = TokenManager.getTokenData();

            if (tokenData.validated) {
                window.location.replace(getAuthenticatedRedirectUrl(true));
                return;
            }

            setEmail(tokenData.email || "");

            const success = await requestVerificationEmail();

            if (success) {
                setHasRequestedCode(true);
                setResendCooldown(120);
            }
        }

        sendCodeOnEntry();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, hasRequestedCode]);

    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }

        const timer = window.setTimeout(() => {
            setResendCooldown((currentValue) => currentValue - 1);
        }, 1000);

        return () => {
            window.clearTimeout(timer);
        };
    }, [resendCooldown]);

    const handleCodeComplete = async (completeCode: string) => {
        if (isSubmittingCode) {
            return;
        }

        setIsSubmittingCode(true);

        const success = await validateUserEmail({ code: completeCode });

        if (!success) {
            setCode("");
            setIsSubmittingCode(false);
            return;
        }

        const credentials = await refreshAccessToken();

        if (!credentials) {
            setCode("");
            setIsSubmittingCode(false);
            return;
        }

        TokenManager.setAccessToken(credentials.accessToken, credentials.refreshToken);
        window.location.replace(getAuthenticatedRedirectUrl(true));
    };

    const goToLogin = () => {
        TokenManager.clear();
        window.location.href = getFullPath("/login");
    };

    const handleResendEmail = async () => {
        const success = await requestVerificationEmail();

        if (success) {
            setResendCooldown(120);
            setCode("");
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: "blue" }}>
                        <MailOutlineIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Validar email
                    </Typography>
                    <Typography sx={{ mt: 2, textAlign: "center" }}>
                        Enviamos um codigo de 6 digitos para {email || "o seu email"}.
                    </Typography>
                    <Typography sx={{ mt: 1, mb: 3, textAlign: "center" }}>
                        Digite ou cole o codigo abaixo para concluir a validacao.
                    </Typography>

                    <OtpInput
                        value={code}
                        onChange={setCode}
                        onComplete={handleCodeComplete}
                        disabled={!isAuthenticated || isSubmittingCode}
                    />

                    <Button
                        type="button"
                        variant="contained"
                        sx={{ mt: 3, mb: 3 }}
                        disabled={resendCooldown > 0 || !isAuthenticated}
                        onClick={handleResendEmail}
                    >
                        {resendCooldown > 0
                            ? `Reenviar email em ${formatCountdown(resendCooldown)}`
                            : "Reenviar email"}
                    </Button>
                    <Link
                        component={Button}
                        onClick={goToLogin}
                        underline="hover"
                        sx={{ textTransform: "none" }}
                    >
                        Pagina de login
                    </Link>
                </Box>
            </Container>
        </ThemeProvider>
    );
}
